import {
  configure,
  createAccessGroup,
  getAllAccessGroups,
  getUsersStateless,
  identity,
  NOTIFICATION_EVENTS,
  User,
} from "deso-protocol";
import { Loader2 } from "lucide-react";
import { Suspense, useEffect, useRef } from "react";
import { lazyWithReload } from "./utils/lazy-with-reload";
import { Toaster } from "sonner";
import { Header } from "./components/header";
import { InstallPrompt } from "./components/install-prompt";
import { MessagingApp } from "./components/messaging-app";
import { SwUpdatePrompt } from "./components/sw-update-prompt";
import { RouteErrorBoundary } from "./components/route-error-boundary";

// bundle-dynamic-imports: Lazy-load route pages so GSAP and page code
// stay out of the main chunk. Logged-in users never download landing page code.
// Uses lazyWithReload to auto-reload once on stale-chunk errors after deploys.
const LandingPage = lazyWithReload(() =>
  import("./components/landing-page").then((m) => ({ default: m.LandingPage }))
);
const LegalPage = lazyWithReload(() =>
  import("./components/legal-page").then((m) => ({ default: m.LegalPage }))
);
const SupportPage = lazyWithReload(() =>
  import("./components/support-page").then((m) => ({ default: m.SupportPage }))
);
const JoinGroupPage = lazyWithReload(
  () => import("./components/join-group-page")
);
const CommunityPage = lazyWithReload(
  () => import("./components/community-page")
);
const FaqPage = lazyWithReload(() => import("./components/faq-page"));
const AboutPage = lazyWithReload(() => import("./components/about-page"));
const ComparePage = lazyWithReload(() => import("./components/compare-page"));
const NotFoundPage = lazyWithReload(
  () => import("./components/not-found-page")
);
const BlogIndex = lazyWithReload(() => import("./components/blog/blog-index"));

// bundle-dynamic-imports: Lazy-load modals — only downloaded when triggered
const LazyBugReportModal = lazyWithReload(() =>
  import("./components/bug-report-modal").then((m) => ({
    default: m.BugReportModal,
  }))
);
const LazyFeedbackModal = lazyWithReload(() =>
  import("./components/feedback-modal").then((m) => ({
    default: m.FeedbackModal,
  }))
);
import { lazyPost } from "./components/blog/blog-registry";
import { AppUser, useStore } from "./store";
import { useShallow } from "zustand/react/shallow";
import { withAuth } from "./utils/with-auth";
import {
  DEFAULT_KEY_MESSAGING_GROUP_NAME,
  DESO_NETWORK,
  getTransactionSpendingLimits,
} from "./utils/constants";
import {
  getCachedUserProfile,
  cacheUserProfile,
  clearCacheForUser,
  checkCacheVersion,
} from "./services/cache.service";
import { clearDecryptionCaches } from "./services/conversations.service";
import { useRedirectFlow } from "./utils/safe-login";
import { applyThemeClass, isAppThemePath } from "./utils/theme";

configure({
  identityURI: import.meta.env.VITE_IDENTITY_URL,
  nodeURI: import.meta.env.VITE_NODE_URL,
  network: DESO_NETWORK,
  spendingLimitOptions: getTransactionSpendingLimits(""),
  ...(useRedirectFlow && {
    redirectURI: window.location.origin + window.location.pathname,
  }),
});

// The SDK constructor calls handleRedirectURI(location.search) when search
// params exist, but that runs BEFORE configure() — so redirectURI isn't set
// yet. Call it again AFTER configure(), but ONLY when the URL actually has
// identity redirect params. Calling it on clean URLs passes undefined to
// URLSearchParams which crashes in some WebKit versions.
// Not gated on useRedirectFlow — desktop users may have been redirected by
// the popup-blocked fallback in safeLogin().
if (window.location.search.includes("service=identity")) {
  identity.handleRedirectURI(window.location.href);
}

// Check cache version — clears stale data on schema changes
checkCacheVersion();

// If opened from a push notification, stash the conversation key so
// messaging-app can navigate to it after conversations load.
const conversationParam = new URLSearchParams(window.location.search).get(
  "conversation"
);
if (conversationParam) {
  useStore.getState().setPendingConversationKey(conversationParam);
  window.history.replaceState({}, "", window.location.pathname);
}

function App() {
  // ── Hydrate a logged-in user (cache-first, background revalidation) ──
  const hydrateUser = useRef<
    ((publicKey: string, messagingKey: string) => void) | null
  >(null);
  hydrateUser.current = (
    publicKey: string,
    messagingPublicKeyBase58Check: string
  ) => {
    const { setAppUser, setIsLoadingUser } = useStore.getState();
    let pollingId = 0;

    useStore.getState().resetChatRequestState();

    const cached = getCachedUserProfile(publicKey);
    const hasDefaultGroup = cached?.allAccessGroups.some(
      (g) => g.AccessGroupKeyName === DEFAULT_KEY_MESSAGING_GROUP_NAME
    );

    if (cached && hasDefaultGroup) {
      // Cache hit — render immediately, refresh in background
      setAppUser(cached.appUser);
      useStore.setState({ allAccessGroups: cached.allAccessGroups });
      setIsLoadingUser(false);

      Promise.all([
        getUser(publicKey),
        getAllAccessGroups({ PublicKeyBase58Check: publicKey }),
      ])
        .then(([userRes, { AccessGroupsOwned, AccessGroupsMember }]) => {
          const user: User | null = userRes.UserList?.[0] ?? null;
          if (!user) return;
          const freshAppUser: AppUser = {
            ...user,
            messagingPublicKeyBase58Check,
            accessGroupsOwned: AccessGroupsOwned,
          };
          const allGroups = (AccessGroupsOwned || []).concat(
            AccessGroupsMember || []
          );
          setAppUser(freshAppUser);
          useStore.setState({ allAccessGroups: allGroups });
          cacheUserProfile(publicKey, freshAppUser, allGroups);

          window.clearInterval(pollingId);
          if (user.BalanceNanos === 0) {
            pollingId = window.setInterval(async () => {
              getUser(publicKey).then((res) => {
                const u = res.UserList?.[0];
                if (u && u.BalanceNanos > 0) {
                  setAppUser({ ...u, messagingPublicKeyBase58Check });
                  window.clearInterval(pollingId);
                }
              });
            }, 3000);
          }
        })
        .catch(() => {
          /* Background refresh failed — cached data is still showing */
        });
    } else {
      // Cache miss or first-time setup — blocking flow
      setIsLoadingUser(true);
      Promise.all([
        getUser(publicKey),
        getAllAccessGroups({ PublicKeyBase58Check: publicKey }),
      ])
        .then(([userRes, { AccessGroupsOwned, AccessGroupsMember }]) => {
          if (
            !AccessGroupsOwned?.find(
              ({ AccessGroupKeyName }) =>
                AccessGroupKeyName === DEFAULT_KEY_MESSAGING_GROUP_NAME
            )
          ) {
            return withAuth(() =>
              createAccessGroup({
                AccessGroupOwnerPublicKeyBase58Check: publicKey,
                AccessGroupPublicKeyBase58Check: messagingPublicKeyBase58Check,
                AccessGroupKeyName: DEFAULT_KEY_MESSAGING_GROUP_NAME,
              })
            ).then(() =>
              getAllAccessGroups({ PublicKeyBase58Check: publicKey }).then(
                (groups) => {
                  const user: User | null = userRes.UserList?.[0] ?? null;
                  const appUser: AppUser | null = user
                    ? {
                        ...user,
                        messagingPublicKeyBase58Check,
                        accessGroupsOwned: groups.AccessGroupsOwned,
                      }
                    : null;
                  const allGroups = (AccessGroupsOwned || []).concat(
                    AccessGroupsMember || []
                  );
                  setAppUser(appUser);
                  useStore.setState({ allAccessGroups: allGroups });
                  if (appUser) cacheUserProfile(publicKey, appUser, allGroups);
                  return user;
                }
              )
            );
          } else {
            const user: User | null = userRes.UserList?.[0] ?? null;
            const appUser: AppUser | null = user
              ? {
                  ...user,
                  messagingPublicKeyBase58Check,
                  accessGroupsOwned: AccessGroupsOwned,
                }
              : null;
            const allGroups = (AccessGroupsOwned || []).concat(
              AccessGroupsMember || []
            );
            setAppUser(appUser);
            useStore.setState({ allAccessGroups: allGroups });
            if (appUser) cacheUserProfile(publicKey, appUser, allGroups);
            return user;
          }
        })
        .then((user) => {
          if (!user) return;
          window.clearInterval(pollingId);
          if (user.BalanceNanos === 0) {
            pollingId = window.setInterval(async () => {
              getUser(publicKey).then((res) => {
                const u = res.UserList?.[0];
                if (u && u.BalanceNanos > 0) {
                  setAppUser({ ...u, messagingPublicKeyBase58Check });
                  window.clearInterval(pollingId);
                }
              });
            }, 3000);
          }
        })
        .catch((err) => {
          console.error("[ChatOn] Failed to hydrate user:", err);
        })
        .finally(() => {
          setIsLoadingUser(false);
        });
    }
  };

  const { setAppUser, setIsLoadingUser } = useStore(
    useShallow((s) => ({
      setAppUser: s.setAppUser,
      setIsLoadingUser: s.setIsLoadingUser,
    }))
  );

  useEffect(() => {
    // ── Snapshot-based init (like Diamond app) ──
    // Use identity.snapshot() to read current state synchronously from
    // localStorage. No iframe, no callbacks, no waiting. Instant.
    try {
      const { currentUser } = identity.snapshot() as { currentUser: any };
      if (currentUser) {
        const messagingKey =
          currentUser.primaryDerivedKey?.messagingPublicKeyBase58Check;
        if (messagingKey) {
          hydrateUser.current?.(currentUser.publicKey, messagingKey);
        } else {
          console.warn(
            "[ChatOn] Missing messagingPublicKeyBase58Check — resetting"
          );
          setIsLoadingUser(false);
        }
      } else {
        // No user in localStorage — logged out. Render landing page instantly.
        setIsLoadingUser(false);
      }
    } catch (err) {
      console.error("[ChatOn] identity.snapshot() failed:", err);
      setIsLoadingUser(false);
    }

    // ── Subscribe for ongoing state changes (login, logout, user switch) ──
    // This handles events AFTER initial load — not needed for boot.
    identity
      .subscribe(({ event, currentUser, alternateUsers }) => {
        const store = useStore.getState();

        // Ignore intermediate authorization events — only act on final states
        if (
          event === NOTIFICATION_EVENTS.AUTHORIZE_DERIVED_KEY_START ||
          event === NOTIFICATION_EVENTS.AUTHORIZE_DERIVED_KEY_END ||
          event === NOTIFICATION_EVENTS.LOGIN_START ||
          event === NOTIFICATION_EVENTS.REQUEST_PERMISSIONS_START ||
          event === NOTIFICATION_EVENTS.REQUEST_PERMISSIONS_END
        ) {
          return;
        }

        // User logged out
        if (event === NOTIFICATION_EVENTS.LOGOUT_END) {
          const loggedOutKey = store.appUser?.PublicKeyBase58Check;
          if (loggedOutKey) clearCacheForUser(loggedOutKey);
          clearDecryptionCaches();

          if (alternateUsers && Object.keys(alternateUsers).length > 0) {
            const fallbackUser = Object.values(alternateUsers)[0]!;
            identity.setActiveUser(fallbackUser.publicKey);
          } else {
            setAppUser(null);
            setIsLoadingUser(false);
          }
          useStore.getState().resetChatRequestState();
          return;
        }

        // No user — logged out.
        // Only clear appUser if an explicit logout event occurred. Transient
        // subscribe callbacks with currentUser=null (e.g. identity iframe
        // reinitializing on PWA resume) must NOT blank the screen.
        if (!currentUser) {
          if (!store.appUser) {
            // No user was loaded yet — safe to stay in logged-out state
            setIsLoadingUser(false);
          }
          // If appUser is already set, ignore this spurious null — the user
          // is still logged in and we already have their data.
          return;
        }

        // New user logged in or switched (different from current appUser)
        if (
          currentUser.publicKey !== store.appUser?.PublicKeyBase58Check &&
          [
            NOTIFICATION_EVENTS.LOGIN_END,
            NOTIFICATION_EVENTS.CHANGE_ACTIVE_USER,
          ].includes(event)
        ) {
          clearDecryptionCaches();
          const messagingKey =
            currentUser.primaryDerivedKey?.messagingPublicKeyBase58Check;
          // Navigate to root if logging in from a public route.
          // Exclude /join — that page handles its own redirect after the join flow completes.
          const p = window.location.pathname;
          if (p !== "/" && !p.startsWith("/join")) {
            window.history.replaceState(null, "", "/");
          }
          if (messagingKey) {
            hydrateUser.current?.(currentUser.publicKey, messagingKey);
          } else {
            console.warn(
              "[ChatOn] Missing messagingPublicKeyBase58Check — resetting"
            );
            setAppUser(null);
            setIsLoadingUser(false);
          }
          return;
        }

        // Same user re-authorized (permissions upgrade) — just clear loading
        if (
          currentUser.publicKey === store.appUser?.PublicKeyBase58Check &&
          store.isLoadingUser
        ) {
          setIsLoadingUser(false);
        }
      })
      .catch((err) => {
        console.error("[ChatOn] identity.subscribe failed:", err);
        // Only clear loading state — don't blank the screen by setting
        // appUser=null if the user is already loaded from snapshot.
        const store = useStore.getState();
        if (!store.appUser) {
          setIsLoadingUser(false);
        }
      });
  }, []);

  const { appUser, isLoadingUser, bugReportError, feedbackModalOpen, theme } =
    useStore(
      useShallow((s) => ({
        appUser: s.appUser,
        isLoadingUser: s.isLoadingUser,
        bugReportError: s.bugReportError,
        feedbackModalOpen: s.feedbackModalOpen,
        theme: s.theme,
      }))
    );
  const path = window.location.pathname;
  const splashRemovedRef = useRef(false);

  // Keep <html> in sync with the saved theme. Only the in-app shell honors the
  // preference; public/marketing routes always render dark (matching the brand
  // site), so force dark unless a logged-in user is on an app route. The inline
  // script in index.html handles the very first paint; this covers state
  // changes (login, toggle) and corrects any direct deep-link into a route.
  const appThemed = !!appUser && isAppThemePath(path);
  const effectiveTheme = appThemed ? theme : "dark";
  useEffect(() => {
    applyThemeClass(effectiveTheme);
  }, [effectiveTheme]);

  // Remove splash once content is ready (not during loading)
  const isJoinRoute = path === "/join" || path.startsWith("/join/");
  const isBlogRoute = path === "/blog" || path.startsWith("/blog/");
  const contentReady =
    !isLoadingUser ||
    !!appUser ||
    path === "/privacy" ||
    path === "/terms" ||
    path === "/donate" ||
    path === "/community" ||
    path === "/faq" ||
    path === "/about" ||
    path === "/compare" ||
    isBlogRoute ||
    isJoinRoute;
  useEffect(() => {
    if (!contentReady || splashRemovedRef.current) return;
    splashRemovedRef.current = true;
    // Wait one frame so the DOM is painted before fading
    requestAnimationFrame(() => {
      const splash = document.getElementById("splash");
      if (!splash) return;
      splash.style.pointerEvents = "none";
      splash.style.transition = "opacity 0.3s ease-out";
      splash.style.opacity = "0";
      const remove = () => splash.remove();
      splash.addEventListener("transitionend", remove);
      // Fallback if transitionend never fires
      setTimeout(remove, 400);
    });
  }, [contentReady]);

  // Safety: remove splash after 5s no matter what (prevents permanent splash on error)
  useEffect(() => {
    const timer = setTimeout(() => {
      const splash = document.getElementById("splash");
      if (!splash) return;
      splash.style.pointerEvents = "none";
      splash.style.transition = "opacity 0.3s ease-out";
      splash.style.opacity = "0";
      setTimeout(() => splash.remove(), 400);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Shared fallback for lazy-loaded route pages — keeps the splash feel
  // instead of flashing a blank white screen on slow connections.
  const routeFallback = (
    <div className="App flex items-center justify-center">
      <Loader2 className="w-11 h-11 animate-spin text-brand" />
    </div>
  );

  // Legal pages are always accessible regardless of auth state
  if (path === "/privacy") {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={routeFallback}>
          <LegalPage type="privacy" />
        </Suspense>
      </RouteErrorBoundary>
    );
  }
  if (path === "/terms") {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={routeFallback}>
          <LegalPage type="terms" />
        </Suspense>
      </RouteErrorBoundary>
    );
  }
  if (path === "/donate") {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={routeFallback}>
          <SupportPage />
          <Toaster position="top-right" theme="dark" />
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  if (path === "/community") {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={routeFallback}>
          <CommunityPage />
          <Toaster position="top-right" theme="dark" />
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  if (path === "/faq") {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={routeFallback}>
          <FaqPage />
          <Toaster position="top-right" theme="dark" />
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  if (path === "/about") {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={routeFallback}>
          <AboutPage />
          <Toaster position="top-right" theme="dark" />
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  if (path === "/compare") {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={routeFallback}>
          <ComparePage />
          <Toaster position="top-right" theme="dark" />
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  if (path === "/blog") {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={routeFallback}>
          <BlogIndex />
          <Toaster position="top-right" theme="dark" />
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  if (path.startsWith("/blog/")) {
    const slug = path.replace("/blog/", "").replace(/\/$/, "");
    const PostComponent = lazyPost(slug);
    if (PostComponent) {
      return (
        <RouteErrorBoundary>
          <Suspense fallback={routeFallback}>
            <PostComponent />
            <Toaster position="top-right" theme="dark" />
          </Suspense>
        </RouteErrorBoundary>
      );
    }
    // Unknown blog slug — show 404
    return (
      <RouteErrorBoundary>
        <Suspense fallback={routeFallback}>
          <NotFoundPage />
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  if (isJoinRoute) {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={routeFallback}>
          <JoinGroupPage />
          <Toaster position="top-right" theme="dark" />
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  const showLanding = !appUser && !isLoadingUser;

  if (showLanding) {
    // Unknown path for logged-out users — show 404 instead of landing page.
    // Allow /chat/<key> deep links to fall through to the landing page so
    // shared chat links can prompt sign-in instead of dead-ending in 404.
    if (path !== "/" && !path.startsWith("/chat/")) {
      return (
        <RouteErrorBoundary>
          <Suspense fallback={routeFallback}>
            <NotFoundPage />
          </Suspense>
        </RouteErrorBoundary>
      );
    }
    return (
      <RouteErrorBoundary>
        <Suspense fallback={routeFallback}>
          <LandingPage />
          <Toaster position="top-right" theme="dark" />
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  if (isLoadingUser && !appUser) {
    return (
      <div className="App flex items-center justify-center">
        <Loader2 className="w-11 h-11 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <>
      <RouteErrorBoundary>
        <div className="App">
          <Header />
          <section className="h-[calc(100%-56px)] mt-[56px] overflow-hidden">
            <MessagingApp />
          </section>
          <InstallPrompt />
          <SwUpdatePrompt />
          <Toaster position="top-right" theme={effectiveTheme} />
          {feedbackModalOpen ? (
            <Suspense fallback={null}>
              <LazyFeedbackModal />
            </Suspense>
          ) : null}
        </div>
      </RouteErrorBoundary>
      {bugReportError ? (
        <Suspense fallback={null}>
          <LazyBugReportModal />
        </Suspense>
      ) : null}
    </>
  );
}

const getUser = async (publicKey: string) =>
  getUsersStateless({
    PublicKeysBase58Check: [publicKey],
    SkipForLeaderboard: true,
    IncludeBalance: true,
    GetUnminedBalance: true,
  });

export default App;
